import React, { useState } from 'react';
import { useProfile } from '../../context/ProfileContext';

// Import award icons
import radiumIcon from '../../assets/radium.png';
import plutoniumIcon from '../../assets/plutonium.png';
import uraniumIcon from '../../assets/uranium.png';

const AwardProgressBar = ({ song }) => {
  const { isDarkMode } = useProfile();
  const [isAwardInfoOpen, setIsAwardInfoOpen] = useState(false);
  const stats = song.stats || {};
  
  // Safe fallback for downloads
  const totalDownloads = stats.totalDownloads || song.downloads || 0;
  const currentAward = stats.currentAward || null; 
  
  const awardTiers = {
    radium: { 
      name: "Radium", 
      downloads: 10, 
      icon: radiumIcon, 
      color: "bg-green-400 dark:bg-emerald-400" 
    },
    plutonium: { 
      name: "Plutonium", 
      downloads: 50, 
      icon: plutoniumIcon, 
      color: "bg-yellow-400 dark:bg-amber-400" 
    },
    uranium: { 
      name: "Uranium", 
      downloads: 100, 
      icon: uraniumIcon, 
      color: "bg-pink-500 dark:bg-brand-pink" 
    },
  };

  const getNextAward = () => {
    if (totalDownloads < 10) return awardTiers.radium;
    if (totalDownloads < 100) return awardTiers.plutonium;
    if (totalDownloads < 1000) return awardTiers.uranium;
    return null; // All awards achieved
  };

  const getPreviousAwardDownloads = () => {
    if (totalDownloads < 10) return 0;
    if (totalDownloads < 100) return 10;
    if (totalDownloads < 1000) return 100;
    return 1000;
  };

  const nextAward = getNextAward();
  const prevDownloads = getPreviousAwardDownloads();
  
  if (!nextAward) {
    return (
      <div className={`p-4 rounded-xl text-center border transition-all duration-500 ${
        isDarkMode 
          ? "bg-zinc-950/40 border-zinc-800/80 text-zinc-100" 
          : "bg-gray-50 border-gray-100 text-gray-900"
      }`}>
        <h3 className="font-black text-sm tracking-tight bg-gradient-to-r from-yellow-400 via-pink-500 to-purple-500 bg-clip-text text-transparent">
          All Awards Achieved!
        </h3>
        <p className={`text-xs mt-1 font-semibold ${isDarkMode ? "text-zinc-400" : "text-gray-500"}`}>
          {totalDownloads.toLocaleString()} Total Downloads
        </p>
      </div>
    );
  }

  const progress = ((totalDownloads - prevDownloads) / (nextAward.downloads - prevDownloads)) * 100;

  return (
    <>
      <div className={`p-3.5 rounded-xl border transition-colors duration-500 relative ${
        isDarkMode ? "bg-zinc-950/40 border-zinc-800/50" : "bg-gray-50 border-gray-100"
      }`}>

        {/* Help Explainer Icon Trigger (Top Right corner) */}
        <button 
          onClick={() => setIsAwardInfoOpen(true)}
          type="button"
          className={`absolute top-3.5 right-3.5 p-1 rounded-lg border transition-all ${
            isDarkMode 
              ? "border-zinc-850 text-zinc-500 hover:text-zinc-300 hover:bg-zinc-900" 
              : "border-gray-200/60 text-gray-400 hover:text-gray-600 hover:bg-white"
          }`}
          title="Nuclear Certifications Help"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </button>
      
        {/* Top Section: Badge Icon on left, Title on right */}
        <div className="flex items-center space-x-3 mb-3 pr-6">
          {/* Dynamic Icon Badge */}
          <div className={`flex-shrink-0 p-1 rounded-lg border ${
            isDarkMode ? "bg-zinc-900/80 border-zinc-800" : "bg-white border-gray-200"
          }`}>
            <img 
              src={currentAward ? awardTiers[currentAward].icon : nextAward.icon} 
              alt="Award Badge" 
              className="w-10 h-10 object-contain"
            />
          </div>

          {/* Text Details (Title + Target) */}
          <div className="min-w-0 flex-grow">
            <h4 className={`text-xs font-bold leading-tight truncate ${
              isDarkMode ? "text-zinc-300" : "text-gray-700"
            }`}>
              "{song.title}"
            </h4>
            <p className="text-[10px] font-medium opacity-50 mt-0.5">
              Progress to {nextAward.name} Certification ({totalDownloads}/{nextAward.downloads} Downloads)
            </p>
          </div>
        </div>

        {/* Bottom Section: Full Width Progress bar and Percent label */}
        <div className="space-y-1.5">
          <div className="flex justify-between items-center text-[10px] font-black">
            <span className="opacity-40 uppercase tracking-wider">Certified Nuclear Progress</span>
            <span className={isDarkMode ? "text-brand-pink" : "text-gray-800"}>
              {Math.floor(progress)}%
            </span>
          </div>

          {/* Progress Slider Track */}
          <div className={`w-full rounded-full h-3 p-[2px] ${
            isDarkMode ? "bg-zinc-800/60" : "bg-gray-200"
          }`}>
            <div
              className={`${nextAward.color} h-full rounded-full transition-all duration-500 ease-out`}
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

      </div>

      {/* Explainer Modal */}
      {isAwardInfoOpen && (
        <div 
          className="fixed inset-0 bg-black/60 flex justify-center items-center z-[150] p-4"
          onClick={() => setIsAwardInfoOpen(false)}
        >
          <div 
            className={`rounded-2xl shadow-2xl p-6 max-w-md w-full border ${
              isDarkMode ? "bg-zinc-900 border-zinc-800 text-zinc-100" : "bg-white border-gray-100 text-gray-800"
            }`}
            onClick={e => e.stopPropagation()}
          >
            <h3 className="text-lg font-black bg-gradient-to-r from-brand-pink to-purple-500 bg-clip-text text-transparent mb-3">
              Nuclear Awards Explained
            </h3>
            
            <div className="space-y-4 text-xs opacity-85 leading-relaxed">
              <p>
                <strong>Nuclear Certifications</strong> recognize milestones of actual fan downloads unlocked via tip interactions.
              </p>
              
              <div className={`p-3 rounded-xl border text-[11px] ${
                isDarkMode ? "bg-zinc-950/80 border-zinc-800" : "bg-gray-50 border-gray-150"
              }`}>
                <p className="font-extrabold text-brand-pink mb-1">Requirement Check:</p>
                <p>
                  You must ensure the <strong>"Disable AUD Downloads"</strong> checkbox is unticked in your song editing options to allow users to trigger download events.
                </p>
              </div>

              <div className="space-y-2.5 pt-1.5">
                <div className="flex items-center space-x-3">
                  <span className="text-[10px] font-black uppercase bg-green-400/10 text-green-400 px-2 py-0.5 rounded border border-green-400/20">Radium</span>
                  <span><strong>10 unique downloads</strong> triggered by tipping fans.</span>
                </div>
                <div className="flex items-center space-x-3">
                  <span className="text-[10px] font-black uppercase bg-yellow-400/10 text-yellow-400 px-2 py-0.5 rounded border border-yellow-400/20">Plutonium</span>
                  <span><strong>50 unique downloads</strong> triggered by tipping fans.</span>
                </div>
                <div className="flex items-center space-x-3">
                  <span className="text-[10px] font-black uppercase bg-pink-500/10 text-brand-pink px-2 py-0.5 rounded border border-brand-pink/20">Uranium</span>
                  <span><strong>100 unique downloads</strong> triggered by tipping fans.</span>
                </div>
              </div>
            </div>

            <button
              onClick={() => setIsAwardInfoOpen(false)}
              className="mt-6 w-full bg-brand-pink text-white font-bold py-3 px-4 rounded-xl shadow-lg shadow-brand-pink/20 hover:scale-[1.01] active:scale-95 transition-all duration-200"
            >
              Understood
            </button>
          </div>
        </div>
      )}
    </> // ✅ Fix 2: Fragment Closed cleanly
  );
};

export default AwardProgressBar;